import { getEmployeeId } from './auth';

/**
 * Make a GET request to the API
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} [params] - Optional query parameters
 * @returns {Promise<any>} The response data
 * @throws {Error} If the request fails or no employee ID is found
 */
export const get = async (endpoint, params = {}) => {
  const employeeId = getEmployeeId();
  if (!employeeId) {
    throw new Error('No employee ID found. Please login again.');
  }

  const url = new URL(endpoint, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value);
    }
  });

  const response = await fetch(url, {
    headers: {
      'X-Employee-ID': employeeId,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Make a POST request to the API
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} data - The data to send
 * @returns {Promise<any>} The response data
 * @throws {Error} If the request fails or no employee ID is found
 */
export const post = async (endpoint, data) => {
  const employeeId = getEmployeeId();
  if (!employeeId) {
    throw new Error('No employee ID found. Please login again.');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Employee-ID': employeeId,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Make a PUT request to the API
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} data - The data to send
 * @returns {Promise<any>} The response data
 * @throws {Error} If the request fails or no employee ID is found
 */
export const put = async (endpoint, data) => {
  const employeeId = getEmployeeId();
  if (!employeeId) {
    throw new Error('No employee ID found. Please login again.');
  }

  const response = await fetch(endpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Employee-ID': employeeId,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Make a DELETE request to the API
 * @param {string} endpoint - The API endpoint to call
 * @returns {Promise<any>} The response data
 * @throws {Error} If the request fails or no employee ID is found
 */
export const del = async (endpoint) => {
  const employeeId = getEmployeeId();
  if (!employeeId) {
    throw new Error('No employee ID found. Please login again.');
  }

  const response = await fetch(endpoint, {
    method: 'DELETE',
    headers: {
      'X-Employee-ID': employeeId,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};